import {
  Avatar,
  Box,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { useSearchParams } from "react-router";
import { format } from "date-fns";
import CDS from "design-system";
import { ShowRichText } from "components/FormElements/Fields/RichTextAreaField";
import { Task } from "features/Resources/Task/models";
import { useMarkCompleteTask, useUpdateTask } from "features/Resources/Task/hooks";
import { TASK_STATUSES } from "features/Resources/Task/constants";
import { isInPast, parseDateOnly } from "helpers/time";
import TaskStartField from "features/Resources/Task/Components/Fields/TaskStartField";
import TaskActionButtons from "features/Resources/Task/Components/TaskActionButtons";
import AssigneeSelect from "features/Resources/Task/Components/AssigneeSelect";
import { CollapsibleHistory } from "features/PlatformEvents";

const ALLOWED_STATUSES = (["active", "in_progress", "blocked", "completed", "skipped"] as Task["status"][]).map(
  (value) => {
    const { value: _v, ...statusInfo } = TASK_STATUSES.get(value)!;
    return { value, ...statusInfo };
  },
);


interface TaskDrawerFormProps {
  task: Task;
}

export default function TaskDrawerForm({ task }: TaskDrawerFormProps) {
  const dueDateInputRef = useRef<HTMLInputElement>(null);
  const [, setSearchParams] = useSearchParams();
  const markComplete = useMarkCompleteTask(task);
  const { mutate: updateTask } = useUpdateTask(task?.id);

  const isUpcoming = task?.status === "upcoming";

  const handleSkip = useCallback(() => {
    if (!task) return;
    const effectiveStartDate = task.startedAt || task.startAt;
    const reopenStatus = isInPast(effectiveStartDate) ? "active" : "upcoming";
    updateTask({ status: task.status === "skipped" ? reopenStatus : "skipped" });
  }, [task, updateTask]);

  const handleActionClick = useCallback(() => {
    if (!task) return;
    setSearchParams({ task_action: task.id });
  }, [task, setSearchParams]);

  const handleDuePillClick = useCallback(() => {
    const input = dueDateInputRef.current;
    if (!input) return;
    if (typeof (input as HTMLInputElement & { showPicker?: () => void }).showPicker === "function") {
      (input as HTMLInputElement & { showPicker: () => void }).showPicker();
    } else {
      input.click();
    }
  }, []);

  if (!task) return null;

  return (
    <Flex direction="column" gap={4}>
      <TaskActionButtons
        task={task}
        onMarkComplete={markComplete}
        onSkip={handleSkip}
        onActionClick={handleActionClick}
      />

      <Flex gap={2} flexWrap="wrap" align="center">
        {/* Status — selectable (disabled when upcoming) */}
        <Menu>
          <MenuButton
            as={CDS.BasePill}
            opacity={isUpcoming ? 0.5 : 1}
            cursor={isUpcoming ? "not-allowed" : "pointer"}
            _hover={isUpcoming ? {} : undefined}
            pointerEvents={isUpcoming ? "none" : "auto"}
          >
            <Flex direction="column" gap="0">
              <CDS.Text10 color="content.tertiary" fontWeight="500" lineHeight="1.3">
                Status
              </CDS.Text10>
              <HStack spacing="1" align="center">
                <Box
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg={TASK_STATUSES.get(task.status)?.colors ?? "content.primary"}
                  flexShrink={0}
                />
                <CDS.Text12 fontWeight="700" color="content.primary" lineHeight="1.3">
                  {TASK_STATUSES.get(task.status)?.label ?? ""}
                </CDS.Text12>
              </HStack>
            </Flex>
          </MenuButton>
          <MenuList fontSize="sm" minW="140px">
            {ALLOWED_STATUSES.map((s) => (
              <MenuItem
                key={s.value}
                fontWeight={task.status === s.value ? "700" : "normal"}
                onClick={() => updateTask({ status: s.value })}
                icon={
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg={s.colors ?? "content.primary"}
                    flexShrink={0}
                  />
                }
              >
                {s.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {/* Assignee — pill opens popover with search + user list */}
        <Popover placement="bottom-start" isLazy>
          {({ onClose }) => (
            <>
              <PopoverTrigger>
                <CDS.BasePill alignItems="center" gap={2}>
                  <Avatar
                    name={task.assignee?.fullName}
                    src={task.assignee?.avatarUrl ?? undefined}
                    size="xs"
                    bg="cdsIndigo.300"
                    color="white"
                    flexShrink={0}
                  />
                  <Flex direction="column" gap="0">
                    <CDS.Text10 color="content.tertiary" fontWeight="500" lineHeight="1.3">
                      Assignee
                    </CDS.Text10>
                    <CDS.Text12 fontWeight="700" color="content.primary" lineHeight="1.3">
                      {task.assignee?.fullName ?? "Unassigned"}
                    </CDS.Text12>
                  </Flex>
                </CDS.BasePill>
              </PopoverTrigger>
              {/* overflow visible so the inline menu list isn't clipped */}
              <PopoverContent w="260px" overflow="visible">
                <PopoverBody p={2} overflow="visible">
                  <AssigneeSelect
                    assignee={task.assignee}
                    onSelect={(id) => {
                      updateTask({ assigneeId: id });
                      onClose();
                    }}
                  />
                </PopoverBody>
              </PopoverContent>
            </>
          )}
        </Popover>

        {/* Starts — read only */}
        <TaskStartField task={task} />

        {/* Due — hidden native date input, pill triggers picker directly */}
        <CDS.BasePill onClick={handleDuePillClick} position="relative">
          <Flex direction="column" gap="0">
            <CDS.Text10 color="content.tertiary" fontWeight="500" lineHeight="1.3">
              Due
            </CDS.Text10>
            <Box
              display="inline-block"
              bg={task.isOverdue ? "statusRed.bg" : "transparent"}
              borderRadius="4px"
              px={task.isOverdue ? "4px" : 0}
            >
              <CDS.Text12 fontWeight="700" color="content.primary" lineHeight="1.3">
                {task.dueOn ? format(parseDateOnly(task.dueOn), "MMM d, yyyy") : task.dueImmediately ? "Immediately" : "—"}
              </CDS.Text12>
            </Box>
          </Flex>
          <input
            ref={dueDateInputRef}
            type="date"
            style={{ position: "absolute", opacity: 0, width: 0, height: 0, top: 0, left: 0, pointerEvents: "none" }}
            value={task.dueOn ? format(parseDateOnly(task.dueOn), "yyyy-MM-dd") : ""}
            onChange={(e) => {
              if (e.target.value) {
                const [year, month, day] = e.target.value.split("-").map(Number);
                updateTask({ dueOn: new Date(year, month - 1, day) });
              }
            }}
          />
        </CDS.BasePill>
      </Flex>

      <ShowRichText label="Description" richText={task.descriptionDisplay ?? task.description ?? ""} />
      <CollapsibleHistory entityId={task.id} entityType="Task" eventType="task" />
    </Flex>
  );
}
